import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { noop } from 'lodash-es';
import { useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLinkStanfordTaxServiceTemplate } from '~/hooks/reactQuery/mutations/useLinkStanfordTaxServiceTemplate';
import { QueryKeys } from '~/hooks/reactQuery/queryKeys';
import { useLocaleKeys } from '~/i18n';
import { ErrorResponse, ServerError } from '~/infra/error-response/ErrorResponse';
import { ServiceTemplateType } from '~/models/service-template';
import { selectors } from '~/store/state-selectors';
import { useStoreActions, useStoreStatic } from '~/store/store-context';
export const GetUseServiceTemplateApiTID = (parentTestId)=>getTestIds(parentTestId);
export const useServiceTemplateApi = ()=>{
    const { showErrorToast } = useOverlayContext();
    const { t: templateTexts } = useLocaleKeys('service.templateApi');
    const { getState } = useStoreStatic();
    const { api, updateBusinessClientData } = useStoreActions();
    const { linkStanfordTaxServiceTemplate } = useLinkStanfordTaxServiceTemplate({
        failureToast: {
            message: templateTexts.stanfordTaxMappingNotSaved(),
            testId: GetUseServiceTemplateApiTID().UseServiceTemplateApiTID.StanfordTaxLinkFailedToast()
        }
    });
    const queryClient = useQueryClient();
    return useMemo(()=>{
        const getBusinessId = ()=>selectors.user.businessId(getState());
        function resetServicesReferenceFieldLookups() {
            const businessId = getBusinessId();
            [
                QueryKeys.ReferenceFieldNames,
                QueryKeys.ReferenceFieldValueInUse
            ].forEach((queryKey)=>{
                void queryClient.resetQueries({
                    queryKey: [
                        queryKey,
                        businessId,
                        'services'
                    ]
                });
            });
        }
        const handleServiceTemplateExistsError = (err, serviceTemplateName)=>{
            if (new ErrorResponse(err).hasServerError(ServerError.ServiceTemplateAlreadyExists)) {
                showErrorToast(templateTexts.serviceNameExists({
                    serviceTemplateName
                }));
            }
            throw err;
        };
        async function updateModifiedAServiceTemplate(value) {
            const bid = getBusinessId();
            const { modifiedAServiceTemplate } = selectors.user.businessClientData(getState(), bid) || {};
            if (bid && !modifiedAServiceTemplate) {
                await updateBusinessClientData(bid, {
                    modifiedAServiceTemplate: true
                });
            }
            return value;
        }
        async function linkStanfordTaxMappingChange(serviceTemplateId, serviceTemplate) {
            const change = serviceTemplate.integrations.stanfordTaxMappingChange();
            if (change) {
                await linkStanfordTaxServiceTemplate({
                    serviceTemplateId,
                    mapping: change.mapping
                }).catch(noop);
            }
        }
        function get(opts) {
            return api.serviceTemplate.getAll(getBusinessId(), opts);
        }
        return {
            getActive: async ()=>get({
                    includeDeleted: false,
                    includeAdhocCharges: false,
                    type: [
                        ServiceTemplateType.Service,
                        ServiceTemplateType.Bundle
                    ]
                }).then((r)=>r.active),
            getForBilling: async ()=>get({
                    includeDeleted: false,
                    includeAdhocCharges: true,
                    type: [
                        ServiceTemplateType.Service,
                        ServiceTemplateType.Bundle
                    ]
                }).then((r)=>r.raw),
            getForMappingToAccountingIntegration: async ()=>get({
                    includeDeleted: true,
                    includeAdhocCharges: true,
                    type: [
                        ServiceTemplateType.Service,
                        ServiceTemplateType.Bundle
                    ]
                }).then((r)=>r.raw),
            getForMappingToPracticeManagementIntegration: async ()=>get({
                    includeDeleted: true,
                    includeAdhocCharges: true,
                    type: [
                        ServiceTemplateType.Service
                    ]
                }).then((r)=>r.raw),
            getForMappingToStanfordTaxIntegration: async ()=>get({
                    includeDeleted: true,
                    includeAdhocCharges: false,
                    type: [
                        ServiceTemplateType.Service
                    ]
                }).then((r)=>r.raw),
            getForInvoice: (invoiceId)=>get({
                    invoiceId,
                    type: [
                        ServiceTemplateType.Service,
                        ServiceTemplateType.Bundle
                    ]
                }).then((r)=>r.raw),
            getActiveServicesAndBundles: async ()=>get({
                    includeDeleted: false,
                    includeAdhocCharges: false,
                    type: [
                        ServiceTemplateType.Service,
                        ServiceTemplateType.Bundle
                    ]
                }).then((r)=>r.active),
            create (serviceTemplate) {
                return api.serviceTemplate.create(serviceTemplate, getBusinessId()).catch((err)=>handleServiceTemplateExistsError(err, serviceTemplate.name)).then(updateModifiedAServiceTemplate).then(async (created)=>{
                    resetServicesReferenceFieldLookups();
                    await linkStanfordTaxMappingChange(created.id, serviceTemplate);
                    return created;
                });
            },
            update (serviceTemplate) {
                return api.serviceTemplate.update(serviceTemplate, getBusinessId()).catch((err)=>handleServiceTemplateExistsError(err, serviceTemplate.name)).then(updateModifiedAServiceTemplate).then(()=>{
                    resetServicesReferenceFieldLookups();
                    return linkStanfordTaxMappingChange(serviceTemplate.id, serviceTemplate);
                });
            },
            delete (id) {
                return api.serviceTemplate.delete(id, getBusinessId());
            }
        };
    }, [
        linkStanfordTaxServiceTemplate,
        queryClient
    ]);
};
